import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  getAuthTokenFromLocalStorage,
  getAuthUserFromLocalStorage,
} from '../../helper';
import { APIDataProvider, TApiResponse, AdminApiPaths } from '../../api';
import { IStructuredEssayQuestion, ISubQuestion, TQuestionDifficulty } from '.';

export interface IAdminCreateStructuredQuestion {
  subTopics?: Array<{ subTopic: string; paper: string }>;
  difficulty: TQuestionDifficulty;
  source?: string;
  tags?: string[];
  details: string;
  marks: number;
  subQuestions: Array<ISubQuestion>;
}

export interface IUpdateStructuredQuestion {
  id: string;
  subTopics: Array<{ subTopic: string; paper: string }>;
  difficulty: TQuestionDifficulty;
  source?: string;
  marks: number;
  tags?: string[];
  details: string;
  subQuestions: Array<ISubQuestion>;
}

export interface IApproveStructuredEssayQuestion {
  id: string;
  isApproved: boolean;
}

export interface ICreateStructuredEssayQuestion {
  questions: Array<IAdminCreateStructuredQuestion>;
}

export interface IGetStructuredEssayQuestionList {
  subTopicId: string;
  limit: number;
  offset: number;
}

export interface IStructuredEssayQuestionListResponse {
  limit: number;
  offset: number;
  totalDocs: number;
  docs: Array<IStructuredEssayQuestion>;
}

const apiDataProvider = new APIDataProvider();

export const generateStructuredEssayQuestion = createAsyncThunk(
  'admin/structuredEssayQuestions/generate',
  async (data: any) => {
    const response = await apiDataProvider.post<TApiResponse<any>>(
      `${AdminApiPaths.structuredEssayQuestions}/gptQuestion`,
      data
    );

    return response.data;
  }
);

export const adminCreateNewStructuredQuestion = createAsyncThunk(
  'admin/structuredEssayQuestions/create',
  async (data: ICreateStructuredEssayQuestion) => {
    const response = await apiDataProvider.post<
      TApiResponse<IStructuredEssayQuestion[]>
    >(`${AdminApiPaths.structuredEssayQuestions}/create`, data);

    return response.data;
  }
);

const getStructuredEssayQuestionListModule = async (
  data: IGetStructuredEssayQuestionList
) => {
  const path = `${AdminApiPaths.structuredEssayQuestions}/list?limit=${data.limit}&offset=${data.offset}&subTopicId=${data.subTopicId}`;

  const response = await apiDataProvider.get<
    TApiResponse<IStructuredEssayQuestionListResponse>
  >(path);

  return response.data;
};

export const getStructuredEssayQuestionList = createAsyncThunk(
  'admin/structuredEssayQuestions/list',
  getStructuredEssayQuestionListModule
);

export const loadMoreStructuredEssayQuestionList = createAsyncThunk(
  'admin/structuredEssayQuestions/loadMore',
  getStructuredEssayQuestionListModule
);

export const getStructuredEssayQuestionById = createAsyncThunk(
  'admin/structuredEssayQuestions/id',
  async (id: string) => {
    const response = await apiDataProvider.get<
      TApiResponse<IStructuredEssayQuestion>
    >(`${AdminApiPaths.structuredEssayQuestions}/${id}`);

    return response.data;
  }
);

export const approveStructuredEssayQuestion = createAsyncThunk(
  'admin/structuredEssayQuestions/approve',
  async (data: IApproveStructuredEssayQuestion) => {
    const response = await apiDataProvider.put<
      TApiResponse<IStructuredEssayQuestion>
    >(`${AdminApiPaths.structuredEssayQuestions}/approve/${data.id}`, data);

    return response.data;
  }
);

export const deleteStructuredEssayQuestion = createAsyncThunk(
  'admin/structuredEssayQuestions/delete',
  async (data: { id: string }) => {
    const response = await apiDataProvider.put<
      TApiResponse<IStructuredEssayQuestion>
    >(`${AdminApiPaths.structuredEssayQuestions}/delete/${data.id}`, data);

    return response.data;
  }
);

export const updateStructuredEssayQuestion = createAsyncThunk(
  'admin/structuredEssayQuestions/update',
  async (data: IUpdateStructuredQuestion) => {
    const { id, ...apiData } = data;
    const response = await apiDataProvider.put<
      TApiResponse<IStructuredEssayQuestion>
    >(`${AdminApiPaths.structuredEssayQuestions}/${id}`, apiData);

    return response.data;
  }
);

export const generateStructuredEssayQuestionStream = async (
  formData: FormData,
  onQuestion: (question: any) => void,
  onComplete: (threadId: string, totalQuestions: number) => void,
  onError: (error: string) => void
): Promise<void> => {
  const token = getAuthTokenFromLocalStorage();
  const authUser = getAuthUserFromLocalStorage();
  const baseURL = process.env.REACT_APP_TUTOPIYA_BASE_URL_V2;

  const headers: Record<string, string> = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (authUser) {
    headers['user-id'] = authUser._id;
  }

  try {
    const response = await fetch(
      `${baseURL}${AdminApiPaths.structuredEssayQuestions}/gptQuestion/stream`,
      {
        method: 'POST',
        headers,
        body: formData,
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body?.getReader();
    const decoder = new TextDecoder();

    if (!reader) {
      throw new Error('No response body');
    }

    let buffer = '';

    try {
      while (true) {
        // eslint-disable-next-line no-await-in-loop
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        // eslint-disable-next-line no-restricted-syntax
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data.trim()) {
              try {
                const parsed = JSON.parse(data);

                if (parsed.type === 'question') {
                  onQuestion(parsed.question);
                } else if (parsed.type === 'completed') {
                  onComplete(parsed.threadId, parsed.totalQuestions);
                  return;
                } else if (parsed.type === 'error') {
                  onError(parsed.error || 'An error occurred');
                  return;
                } else if (parsed.type === 'connected') {
                  // Connection established
                }
              } catch (e) {
                console.error('Error parsing SSE data:', e);
              }
            }
          }
        }
      }
    } catch (error: any) {
      onError(error?.message || 'Stream reading error');
    }
  } catch (error: any) {
    onError(error?.message || 'Failed to start stream');
  }
};
